/* eslint-disable jira/naming/disallow-filenames */
// eslint-disable-next-line jira/filename-case
import React, { useEffect, useMemo, useRef } from 'react';
import { useRegisterNudgeTarget } from '@atlassiansox/nudge-tooltip/register-nudge-target';
import { InviteTeamNudgeAsync } from '@jira/software__onboarding-nudges/src/InviteTeamNudgeAsync.tsx';
// Taken directly from the teams app definition to be used with global app shortcuts
import { useCreateTeamModal } from '@jira/navigation-apps__sidebar-nav4-sidebars-content-teams/src/ui/use-create-team-modal/index.tsx';
import { useInvitePeopleModal } from '@jira/navigation-apps__sidebar-nav4-sidebars-content-teams/src/ui/use-invite-people-modal.tsx';
import { EnablementTouchpointEnum } from '@atlassian/expansion-services/services';
import { FIXED_ITEM_DROPDOWN_ACTION } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/component-type.tsx';
import { LEVEL_TWO } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/level.tsx';
import { GLOBAL_APP_SHORTCUT_TEAMS } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/section-type.tsx';
import { MENU_ID_ANALYTIC_EVENT_IDS } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/side-nav.tsx';
import { MENU_ID_TEAMS } from '@jira/navigation-apps__sidebar-nav4-context/src/common/constants.tsx';
import { getUserNavigationItemLinks } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/utils/get-user-navigation-item-links/index.tsx';
import { JWM_INVITE_TEAM_NUDGE_ID } from '@jira/platform__onboarding-core/src/constants.tsx';
import { Text } from '@atlaskit/primitives/compiled/text';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useTeams } from '@atlassian/global-shortcuts/teams';
import { defineMessage, useIntl } from '@atlassian/jira-intl';
import type { GlobalShortcutLinkItem } from '@atlassian/global-shortcuts/types';
import { fg } from '@atlassian/jira-feature-gating';

const CREATE_A_TEAM = 'createATeam';
const INVITE_PEOPLE = 'invitePeople';

const teamsShortcutCreateTeamMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-teams.common.action-buttons.create-team',

	defaultMessage: 'Create a team',
	description: 'Used in popup menu for user to create a team',
});

const teamsShortcutInvitePeopleMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-teams.common.action-buttons.invite-people',

	defaultMessage: 'Invite people',
	description: 'Used in popup menu for user to invite new user',
});

type TeamsAppShortcutReturn = {
	config: GlobalShortcutLinkItem;
	component: React.JSX.Element;
};

export function useTeamsAppShortcut(): TeamsAppShortcutReturn {
	const USER_NAVIGATION_ITEM_LINKS = getUserNavigationItemLinks();
	const peopleMenuWrapperRef = useRegisterNudgeTarget<HTMLAnchorElement>(
		JWM_INVITE_TEAM_NUDGE_ID,
		fg('teams-app-shortcut-nudge'),
	).ref;
	const teamsConfig = useTeams(USER_NAVIGATION_ITEM_LINKS[MENU_ID_TEAMS], {
		ref: peopleMenuWrapperRef,
	});
	const { formatMessage } = useIntl();
	const invitePeopleModalTrigger = useInvitePeopleModal(EnablementTouchpointEnum.NAV4_TEAMS_ADD);
	const [createTeamModal, createTeamModalTrigger] = useCreateTeamModal();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	// These callback refs ensure the useMemo below doesn't recalculate when
	// unstable function references change across renders. The callbacks are only
	// invoked on user interaction (click handlers), so reading .current at call
	// time is safe and always up-to-date.
	const invitePeopleModalTriggerRef = useRef(invitePeopleModalTrigger);
	const createTeamModalTriggerRef = useRef(createTeamModalTrigger);
	const createAnalyticsEventRef = useRef(createAnalyticsEvent);
	useEffect(() => {
		invitePeopleModalTriggerRef.current = invitePeopleModalTrigger;
		createTeamModalTriggerRef.current = createTeamModalTrigger;
		createAnalyticsEventRef.current = createAnalyticsEvent;
	});

	const component = useMemo(() => {
		return (
			<>
				{fg('teams-app-shortcut-nudge') && <InviteTeamNudgeAsync />}
				{createTeamModal}
			</>
		);
	}, [createTeamModal]);

	const config = useMemo(() => {
		const invitePeopleAction = [
			{
				onClick: (): void => {
					fireUIAnalytics(createAnalyticsEventRef.current({}), {
						action: 'clicked',
						actionSubject: 'dropdownItem',
						actionSubjectId: MENU_ID_ANALYTIC_EVENT_IDS[MENU_ID_TEAMS],
						attributes: {
							level: LEVEL_TWO,
							componentType: FIXED_ITEM_DROPDOWN_ACTION,
							parentItemId: GLOBAL_APP_SHORTCUT_TEAMS,
							itemId: INVITE_PEOPLE,
						},
					});
					invitePeopleModalTriggerRef.current();
				},
				component: <Text>{formatMessage(teamsShortcutInvitePeopleMessage)}</Text>,
			},
		];

		return {
			...teamsConfig,
			actions: [
				...invitePeopleAction,
				{
					onClick: () => {
						fireUIAnalytics(createAnalyticsEventRef.current({}), {
							action: 'clicked',
							actionSubject: 'dropdownItem',
							actionSubjectId: MENU_ID_ANALYTIC_EVENT_IDS[MENU_ID_TEAMS],
							attributes: {
								level: LEVEL_TWO,
								componentType: FIXED_ITEM_DROPDOWN_ACTION,
								parentItemId: GLOBAL_APP_SHORTCUT_TEAMS,
								itemId: CREATE_A_TEAM,
							},
						});
						createTeamModalTriggerRef.current();
					},
					component: <Text>{formatMessage(teamsShortcutCreateTeamMessage)}</Text>,
				},
			],
		};
	}, [teamsConfig, formatMessage]);

	return { config, component };
}
